import {
  KolInsight,
  KolInsightBuzz,
  KolInsightIndicators,
  KolInsightSentiment,
  KolInsightSymbol,
  kolInsightBuzzEnum,
  kolInsightSentimentEnum,
  marketHomeComponentTypeEnum,
} from '@flipster/apis/v2/market/types';
import type { MarketLayout } from '@flipster/apis/v2/market/types';
import { InstrumentProductTypeEnum } from '@flipster/apis/v2/snapshot';

import { flattenMarketLayout } from '../shared/market-layout';

export const marketKolInsightsMaxCards = 3;

/** Card/list footer: show at most this many symbol icons and tickers. */
export const kolInsightMaxVisibleSymbols = 2;

export const kolInsightsQueryParam = 'i';

export type NormalizedKolIndicators = {
  isHot: boolean;
  sentiment: KolInsightSentiment | undefined;
  buzz: KolInsightBuzz | undefined;
};

export function hasKolInsightsLayoutNode(layout: MarketLayout | undefined): boolean {
  return flattenMarketLayout(layout?.components).some(
    (entry) => entry.type === marketHomeComponentTypeEnum.kolInsights,
  );
}

/** Missing / NaN / out-of-range → 0 when length > 0; otherwise 0. */
export function resolveInsightIndex(raw: string | null | undefined, length: number): number {
  if (length <= 0) return 0;
  if (raw == null || raw === '') return 0;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= length) return 0;
  return parsed;
}

export function takeMarketInsights(insights: KolInsight[]): KolInsight[] {
  return insights.slice(0, marketKolInsightsMaxCards);
}

export function normalizeIndicators(indicators: KolInsightIndicators | undefined): NormalizedKolIndicators {
  if (!indicators) {
    return { isHot: false, sentiment: undefined, buzz: undefined };
  }

  const sentimentValues = Object.values(kolInsightSentimentEnum);
  const buzzValues = Object.values(kolInsightBuzzEnum);

  const sentiment =
    typeof indicators.sentiment === 'string' && sentimentValues.includes(indicators.sentiment as KolInsightSentiment)
      ? (indicators.sentiment as KolInsightSentiment)
      : undefined;

  const buzz =
    typeof indicators.buzz === 'string' && buzzValues.includes(indicators.buzz as KolInsightBuzz)
      ? (indicators.buzz as KolInsightBuzz)
      : undefined;

  return {
    isHot: indicators.isHot === true,
    sentiment,
    buzz,
  };
}

export function filterResolvableSymbols<T extends { symbol: string }>(
  symbols: T[],
  isResolvable: (symbol: string) => boolean,
): T[] {
  return symbols.filter((item) => isResolvable(item.symbol));
}

export function getTradePathForSymbol(
  symbol: string,
  productType: string | undefined,
): `/trade/perpetual/${string}` | `/trade/spot/${string}` {
  if (productType === InstrumentProductTypeEnum.Spot) {
    return `/trade/spot/${symbol}`;
  }
  return `/trade/perpetual/${symbol}`;
}

/** Locale-aware day interval label (e.g. "7 days"). */
export function formatPriceChangeIntervalLabel(days: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'unit', unit: 'day', unitDisplay: 'long' }).format(days);
  } catch {
    return `${days} days`;
  }
}

/** Short elapsed label. English `narrow` matches Figma (`14m ago`). */
export function formatRefreshedAtElapsed(from: Date, locale: string, now = new Date()): string {
  const fromMs = from.getTime();
  const nowMs = now.getTime();
  if (Number.isNaN(fromMs) || Number.isNaN(nowMs)) return '';

  const minutes = Math.floor(Math.max(0, nowMs - fromMs) / 60_000);

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { style: 'narrow', numeric: 'always' });
    if (minutes < 60) return rtf.format(-minutes, 'minute');
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return rtf.format(-hours, 'hour');
    return rtf.format(-Math.floor(hours / 24), 'day');
  } catch {
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }
}

export function formatInsightSymbolTickers(
  symbols: KolInsightSymbol[],
  resolveShortName: (symbol: string) => string | undefined,
  max = kolInsightMaxVisibleSymbols,
): string {
  return symbols
    .slice(0, max)
    .map((s) => resolveShortName(s.symbol) ?? s.symbol)
    .join(', ');
}
